import type { TooltipPlacement, TooltipSide } from '../teleport-types';

export const VIEWPORT_PADDING = 8;

export function getViewportLimits(): { maxWidth: number; maxHeight: number } {
    return {
        maxWidth: window.innerWidth - VIEWPORT_PADDING * 2,
        maxHeight: window.innerHeight - VIEWPORT_PADDING * 2,
    };
}

export function parseCssLength(value: string, percentBase: number): number | null {
    const trimmed = value.trim();
    if (trimmed.endsWith('%')) {
        const pct = parseFloat(trimmed);
        return Number.isFinite(pct) ? (percentBase * pct) / 100 : null;
    }
    const num = parseFloat(trimmed);
    return Number.isFinite(num) ? num : null;
}

export function resolveViewportMaxWidth(
    maxWidth: string | undefined,
    refWidth: number,
    viewportMaxW: number,
): number {
    if (maxWidth === '100%') {
        return Math.min(refWidth, viewportMaxW);
    }
    if (maxWidth) {
        const parsed = parseCssLength(maxWidth, viewportMaxW);
        return parsed !== null ? Math.min(parsed, viewportMaxW) : viewportMaxW;
    }
    return viewportMaxW;
}

export function clampToViewport(
    top: number,
    left: number,
    cw: number,
    ch: number,
): { top: number; left: number } {
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    return {
        top: Math.min(Math.max(top, VIEWPORT_PADDING), Math.max(VIEWPORT_PADDING, vh - ch - VIEWPORT_PADDING)),
        left: Math.min(Math.max(left, VIEWPORT_PADDING), Math.max(VIEWPORT_PADDING, vw - cw - VIEWPORT_PADDING)),
    };
}

/** Порядок подбора сторон в режиме auto: снизу, затем сверху, затем бока. */
const AUTO_ORDER: TooltipSide[] = ['bottom', 'top', 'right', 'left'];

export function resolvePlacement(
    preferred: TooltipPlacement,
    rect: DOMRect,
    cw: number,
    ch: number,
    offset: number,
    containInViewport: boolean,
): TooltipSide {
    // Явно заданная сторона — жёсткая привязка: другую не подбираем, даже если
    // места не хватает. Попап в этом случае уйдёт за границу окна — см. calcCoords.
    if (preferred !== 'auto') return preferred;

    const fits: Record<TooltipSide, boolean> = {
        top:    rect.top                         >= ch + offset,
        bottom: window.innerHeight - rect.bottom >= ch + offset,
        left:   rect.left                        >= cw + offset,
        right:  window.innerWidth - rect.right   >= cw + offset,
    };

    const fitting = AUTO_ORDER.find((side) => fits[side]);
    if (fitting) return fitting;

    // Не влезает нигде: попап всё равно частично уйдёт за экран. Тултипу оставляем
    // сторону по умолчанию, широкому попапу (containInViewport) отдаём сторону с
    // наибольшим запасом места — так за границей окажется меньшая его часть.
    if (!containInViewport) return AUTO_ORDER[0];

    const available: Record<TooltipSide, number> = {
        top: rect.top - VIEWPORT_PADDING,
        bottom: window.innerHeight - rect.bottom - VIEWPORT_PADDING,
        left: rect.left - VIEWPORT_PADDING,
        right: window.innerWidth - rect.right - VIEWPORT_PADDING,
    };

    let best: TooltipSide = AUTO_ORDER[0];
    let bestSpace = -Infinity;

    for (const side of AUTO_ORDER) {
        if (available[side] > bestSpace) {
            bestSpace = available[side];
            best = side;
        }
    }

    return best;
}

export function calcCoords(
    placement: TooltipSide,
    rect: DOMRect,
    cw: number,
    ch: number,
    offset: number,
): { top: number; left: number } {
    const refCx = rect.left + rect.width / 2;
    const refCy = rect.top + rect.height / 2;

    let top: number;
    let left: number;

    switch (placement) {
        case 'bottom':
            top = rect.bottom + offset;
            left = refCx - cw / 2;
            break;
        case 'top':
            top = rect.top - ch - offset;
            left = refCx - cw / 2;
            break;
        case 'right':
            top = refCy - ch / 2;
            left = rect.right + offset;
            break;
        case 'left':
            top = refCy - ch / 2;
            left = rect.left - cw - offset;
            break;
    }

    // Прижимаем только поперечную ось: она к выбору стороны не относится, и без
    // неё попап свисал бы вбок просто из-за центрирования по якорю. По главной оси
    // не двигаем никогда — попап честно уходит за границу окна, если места нет.
    // Иначе он отрывается от якоря и прилипает к краю экрана.
    const clamped = clampToViewport(top, left, cw, ch);

    return placement === 'top' || placement === 'bottom'
        ? { top, left: clamped.left }
        : { top: clamped.top, left };
}

export function computeTeleportPosition(options: {
    preferred: TooltipPlacement;
    refRect: DOMRect;
    containerWidth: number;
    containerHeight: number;
    offset?: number;
    containInViewport: boolean;
}): { top: number; left: number; placement: TooltipSide } {
    const offset = options.offset ?? 8;
    const placement = resolvePlacement(
        options.preferred,
        options.refRect,
        options.containerWidth,
        options.containerHeight,
        offset,
        options.containInViewport,
    );
    const { top, left } = calcCoords(
        placement,
        options.refRect,
        options.containerWidth,
        options.containerHeight,
        offset,
    );

    return { top, left, placement };
}

export function isWithinViewport(
    top: number,
    left: number,
    width: number,
    height: number,
): boolean {
    return (
        top >= VIEWPORT_PADDING
        && left >= VIEWPORT_PADDING
        && top + height <= window.innerHeight - VIEWPORT_PADDING
        && left + width <= window.innerWidth - VIEWPORT_PADDING
    );
}
